import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaInbox, FaSearch } from "react-icons/fa";
import Paginations from "../../GenericComponents/Pagination";
import { deleteInquiryBatch, fetchInquiryBatches } from "./inquiryBatchApi";
import EditInquiryBatchModal from "./EditInquiryBatchModal";
import Swal from "sweetalert2";
import {
  BATCH_STATUSES,
  INQUIRY_OPEN_FILTERS,
  formatBatchDateTime,
  getBatchRowToneClass,
  getBatchStatusMeta,
  getPeriodStatusMeta,
} from "./inquiryBatchUtils";

const CELL = "px-3 py-2.5";
const HEAD =
  "border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

function StatusBadge({ status }) {
  const meta = getBatchStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function PeriodBadge({ status }) {
  const meta = getPeriodStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function StudentProgressCell({ total, open, sent, replied }) {
  return (
    <td className={CELL}>
      <p className="mb-1 tabular-nums text-[12px] font-semibold text-slate-800">
        {total} student{total === 1 ? "" : "s"}
      </p>
      <div className="flex flex-wrap gap-1">
        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">
          {open} open
        </span>
        <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-200">
          {sent} sent
        </span>
        <span className="inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-200">
          {replied} replied
        </span>
      </div>
    </td>
  );
}

/**
 * Inquiry Batches list + filters for use inside Commissions page shell.
 */
function InquiryBatchesPanel({ applicationType = "education" }) {
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [inquiryFilter, setInquiryFilter] = useState("open");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 25,
    total: 0,
    lastPage: 1,
  });
  const [error, setError] = useState("");
  const [editBatch, setEditBatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchInquiryBatches({
        page: pagination.currentPage,
        perPage: pagination.perPage,
        status: status || undefined,
        search: appliedSearch || undefined,
        applicationType,
        periodStatus: inquiryFilter,
      });
      setBatches(result.batches || []);
      setPagination((prev) => ({
        ...prev,
        ...(result.pagination || {}),
      }));
    } catch (err) {
      setError(err?.message || "Failed to load inquiry batches.");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.currentPage,
    pagination.perPage,
    status,
    appliedSearch,
    applicationType,
    inquiryFilter,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePaginationCallback = (page, perPage) => {
    if (perPage && perPage !== "") {
      setPagination((prev) => ({
        ...prev,
        perPage: Number(perPage),
        currentPage: 1,
      }));
    } else {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
      }));
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleResetFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setStatus("");
    setInquiryFilter("open");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDeleteBatch = async (batchId) => {
    const confirmed = await Swal.fire({
      title: "Delete batch?",
      text: "Delete this inquiry batch? This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    try {
      setLoading(true);
      setError("");

      await deleteInquiryBatch(batchId);

      await Swal.fire({
        title: "Deleted!",
        text: "Inquiry batch deleted successfully.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#007BFF",
      });

      await load();
    } catch (err) {
      setError(err?.message || "Failed to delete inquiry batch.");
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSaved = async () => {
    await load();
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-gray-200 bg-white p-2.5 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <form
            onSubmit={handleSearchSubmit}
            className="flex min-w-[220px] flex-1 items-center gap-2"
          >
            <div className="relative flex-1">
              <FaSearch
                size={12}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Batch or client/user ID"
                className="h-9 w-full rounded border border-slate-200 bg-white pl-8 pr-2.5 text-[13px] outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-9 items-center rounded-md bg-[#007BFF] px-3 text-[13px] font-semibold text-white hover:bg-[#0069D9]"
              style={{ border: "none", boxShadow: "none" }}
            >
              Search
            </button>
          </form>

          <select
            value={inquiryFilter}
            onChange={(e) => {
              setInquiryFilter(e.target.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            className={`h-9 min-w-[160px] rounded border px-2.5 text-[13px] font-semibold outline-none focus:border-[#007BFF] ${
              inquiryFilter === "open"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-300 bg-slate-50 text-slate-700"
            }`}
          >
            {INQUIRY_OPEN_FILTERS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            className="h-9 rounded border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-700 outline-none focus:border-[#007BFF]"
          >
            <option value="">All batch statuses</option>
            {BATCH_STATUSES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
            style={{ boxShadow: "none" }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-1 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-50" />
          Open inquiry
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-slate-300 bg-slate-100" />
          Closed inquiry
        </span>
      </div>

      {error ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700">
          {error}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
        <div className="cms-commissions-table-wrap overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-[13px]">
            <thead className={HEAD}>
              <tr>
                <th className={CELL}>Batch</th>
                <th className={CELL}>Batch name</th>
                <th className={CELL}>Inquiry</th>
                <th className={CELL}>Status</th>
                <th className={CELL}>Students</th>
                <th className={CELL}>Created</th>
                <th className={CELL}>Updated</th>
                <th className={`${CELL} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`sk-${index}`}>
                    <td className={CELL} colSpan={8}>
                      <div className="h-4 animate-pulse rounded bg-slate-100" />
                    </td>
                  </tr>
                ))
              ) : batches.length > 0 ? (
                batches.map((batch) => {
                  const counts = batch.counts || {};
                  const total =
                    batch.items_count ??
                    counts.total ??
                    (Array.isArray(batch.items) ? batch.items.length : 0);
                  const open =
                    batch.open_items_count ??
                    counts.pending ??
                    counts.open ??
                    0;
                  const sent =
                    batch.sent_items_count ?? counts.sent ?? 0;
                  const replied =
                    batch.replied_items_count ??
                    counts.replied ??
                    0;
                  const periodStatus = batch.period_status || "open";

                  return (
                    <tr
                      key={batch.id}
                      className={`transition ${getBatchRowToneClass(periodStatus)}`}
                    >
                      <td className={CELL}>
                        <p className="mb-0 font-semibold text-slate-900">
                          {batch.batch_no || `Batch #${batch.id}`}
                        </p>
                        <p className="mb-0 text-[11px] text-slate-500">
                          {batch.created_by_name || batch.creator?.name || "—"}
                        </p>
                      </td>
                      <td className={CELL}>
                        <p className="mb-0 line-clamp-2 font-medium text-slate-800">
                          {batch.batch_name || batch.subject || "—"}
                        </p>
                      </td>
                      <td className={CELL}>
                        <PeriodBadge status={periodStatus} />
                      </td>
                      <td className={CELL}>
                        <StatusBadge status={batch.status} />
                      </td>
                      <StudentProgressCell
                        total={total}
                        open={open}
                        sent={sent}
                        replied={replied}
                      />
                      <td
                        className={`${CELL} whitespace-nowrap text-slate-600`}
                      >
                        {formatBatchDateTime(batch.created_at)}
                      </td>
                      <td
                        className={`${CELL} whitespace-nowrap text-slate-600`}
                      >
                        {formatBatchDateTime(batch.updated_at)}
                      </td>
                      <td className={`${CELL} text-right`}>
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditBatch(batch)}
                            className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                            style={{ boxShadow: "none" }}
                          >
                            Edit
                          </button>

                          <Link
                            to={`/commissions/inquiry-batches/${batch.id}`}
                            className="inline-flex h-8 items-center rounded-md border border-[#007BFF]/30 bg-[#007BFF]/5 px-2.5 text-[12px] font-semibold text-[#007BFF] hover:bg-[#007BFF]/10"
                            style={{ boxShadow: "none" }}
                          >
                            Open
                          </Link>

                          <button
                            type="button"
                            onClick={() => handleDeleteBatch(batch.id)}
                            className="inline-flex h-8 items-center rounded-md border border-red-200 bg-red-50 px-2.5 text-[12px] font-semibold text-red-600 hover:bg-red-100"
                            style={{ boxShadow: "none" }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
                        <FaInbox size={16} className="text-gray-400" />
                      </div>
                      <p className="text-[15px] font-semibold text-gray-900">
                        No {inquiryFilter === "open" ? "open" : "closed"}{" "}
                        inquiry batches
                      </p>
                      <p className="mt-0.5 text-[13px] text-gray-500">
                        {inquiryFilter === "open"
                          ? "Create a batch or switch to Closed inquiry."
                          : "Close a batch inquiry or switch to Open inquiry."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && pagination.total > 0 ? (
          <div className="border-t border-gray-100 px-2 py-1">
            <Paginations
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.perPage}
              lastPage={pagination.lastPage}
              onPageChange={handlePaginationCallback}
              totalItems={pagination.total}
              perItem={batches.length}
            />
          </div>
        ) : null}
      </div>

      <EditInquiryBatchModal
        open={Boolean(editBatch)}
        batch={editBatch}
        onClose={() => setEditBatch(null)}
        onSaved={handleBatchSaved}
      />
    </div>
  );
}

export default InquiryBatchesPanel;
